/**
 * Shape checks for values that arrive from outside the type system — parsed JSON, URL params —
 * where a declared type would be a claim nothing verifies.
 */

/** Narrows to an indexable object, so individual fields can be checked without casting. */
export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

/** Parses JSON from outside the type system, yielding `null` rather than throwing. */
export const parseJson = (raw: string): unknown => {
  try {
    return JSON.parse(raw)
  } catch (_) {
    return null
  }
}

/**
 * Finite, so `Infinity` is rejected along with non-numbers. JSON has no `NaN` literal, but
 * `JSON.parse('1e999')` is `Infinity` — and every caller here feeds an id, a timestamp or a group
 * into a request, where `JSON.stringify(Infinity)` silently becomes `null`.
 */
export const isFiniteNumber = (value: unknown): value is number => Number.isFinite(value)

export const isStringValue = (value: unknown): value is string => typeof value === 'string'

export const asFiniteNumber = (value: unknown): number | null =>
  isFiniteNumber(value) ? value : null
export const asString = (value: unknown): string | null => (isStringValue(value) ? value : null)

/** An array whose every entry passes `isItem`; a partial match reads as absent, not as a subset. */
export const asArrayOf = <T>(value: unknown, isItem: (item: unknown) => item is T): T[] | null =>
  Array.isArray(value) && value.every(isItem) ? value : null

/**
 * Reads a sibling field inside a yup `.test()`. Yup declares `TestContext.parent` as `any`, so the
 * narrowing happens here once instead of at every test that compares two fields of one form.
 * Works for both call shapes: pass `context` from an arrow test, or `this` from a function one.
 */
export const yupSiblingString = (context: { parent?: unknown }, field: string): string | null =>
  isRecord(context.parent) ? asString(context.parent[field]) : null
